import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, ArrowLeft, BellOff, Filter, ShieldOff,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─────────────────────────────────────────────────────────────────────
   /admin/ops/notifications — Suppression audit log (Feb 2026 iter #4).
   Read-only surface over `notification_suppressions`. Renders three
   dashboard windows (24h / 7d / 30d) with by-channel / by-feature /
   by-reason rollups plus a filterable paginated table. Backed by:
     GET /api/admin/notifications/suppressions/summary
     GET /api/admin/notifications/suppressions
   ─────────────────────────────────────────────────────────────────── */

const CHANNELS = ['', 'email', 'sms', 'in-app', 'notify'];
const SINCE_OPTIONS = [
  { hours: 24, label: 'Last 24h' },
  { hours: 24 * 7, label: 'Last 7 days' },
  { hours: 24 * 30, label: 'Last 30 days' },
  { hours: 24 * 90, label: 'Last 90 days' },
];

const Tile = ({ label, value, hint, testid }) => (
  <div className="cn-card p-5" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</p>
    <p className="cn-display text-2xl text-cn-text mt-1.5 tabular-nums">{value ?? '—'}</p>
    {hint && <p className="text-[11px] text-cn-text-muted mt-1">{hint}</p>}
  </div>
);

const RollupList = ({ title, rows, emptyLabel = 'None', testidPrefix }) => (
  <div className="cn-card p-5" data-testid={`${testidPrefix}-card`}>
    <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-3">{title}</p>
    {(!rows || rows.length === 0) ? (
      <p className="text-sm text-cn-text-muted">{emptyLabel}</p>
    ) : (
      <ul className="space-y-1.5" data-testid={`${testidPrefix}-list`}>
        {rows.slice(0, 10).map((r, i) => (
          <li key={`${r.key}-${i}`}
              className="flex items-center justify-between gap-3 text-sm">
            <span className="truncate text-cn-text">{r.key || <em>untagged</em>}</span>
            <span className="tabular-nums font-semibold text-cn-text-muted">{r.n}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function AdminNotificationSuppressions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [channel, setChannel] = useState('');
  const [featureFilter, setFeatureFilter] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [sinceHours, setSinceHours] = useState(24 * 7);
  const [limit] = useState(200);

  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setRefreshing(true);
    try {
      const params = { since_hours: sinceHours, limit };
      if (channel) params.channel = channel;
      if (featureFilter.trim()) params.feature = featureFilter.trim();
      if (reasonFilter.trim()) params.reason = reasonFilter.trim();
      const [sumResp, listResp] = await Promise.all([
        axios.get(`${API}/admin/notifications/suppressions/summary`),
        axios.get(`${API}/admin/notifications/suppressions`, { params }),
      ]);
      setSummary(sumResp.data);
      setRows(listResp.data?.rows || []);
      setCount(listResp.data?.count || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load suppressions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isAdmin, channel, featureFilter, reasonFilter, sinceHours, limit]);

  useEffect(() => { load(); }, [load]);

  const activeWindow = useMemo(() => {
    if (!summary) return null;
    const key = sinceHours <= 24 ? '24h' : sinceHours <= 24 * 7 ? '7d' : '30d';
    return summary?.windows?.[key] || null;
  }, [summary, sinceHours]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-cn-bg flex items-center justify-center p-6">
        <div className="cn-card p-8 max-w-md text-center"
             data-testid="admin-suppressions-forbidden">
          <ShieldOff className="w-10 h-10 mx-auto text-cn-text-muted" aria-hidden="true" />
          <h2 className="mt-4 text-xl font-semibold">Admin only</h2>
          <p className="mt-2 text-sm text-cn-text-muted">
            Sign in with an admin account to view the notification
            suppression log.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cn-bg text-cn-text pb-24"
         data-testid="admin-suppressions-page">
      {/* Header */}
      <header className="border-b border-cn-border bg-cn-bg sticky top-0 z-30">
        <div className="cn-container py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 rounded-lg hover:bg-cn-bone-100 dark:hover:bg-cn-ink-800"
              aria-label="Back to admin dashboard"
              data-testid="admin-suppressions-back">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                Ops · Notifications
              </p>
              <h1 className="text-xl font-semibold tracking-tight"
                  data-testid="admin-suppressions-title">
                Suppression log
              </h1>
            </div>
          </div>
          <button
            onClick={load}
            disabled={refreshing}
            className="cn-btn inline-flex items-center gap-2"
            data-testid="admin-suppressions-refresh">
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
        </div>
      </header>

      <div className="cn-container py-8 space-y-8">
        {loading ? (
          <div className="cn-card p-8 flex items-center gap-3"
               data-testid="admin-suppressions-loading">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm text-cn-text-muted">Loading suppression audit…</span>
          </div>
        ) : (
          <>
            {/* Dashboard tiles */}
            <section aria-label="Suppression totals"
                     className="grid gap-4 md:grid-cols-3"
                     data-testid="admin-suppressions-tiles">
              {['24h', '7d', '30d'].map((w) => (
                <Tile
                  key={w}
                  label={`Suppressed · ${w}`}
                  value={summary?.windows?.[w]?.total ?? 0}
                  hint="Refused outbound sends (email + SMS + in-app)"
                  testid={`admin-suppressions-tile-${w}`}
                />
              ))}
            </section>

            {/* Rollups for the active window */}
            <section aria-label="Rollups for the active window"
                     className="grid gap-4 md:grid-cols-3"
                     data-testid="admin-suppressions-rollups">
              <RollupList
                title="By channel"
                rows={activeWindow?.by_channel}
                testidPrefix="admin-suppressions-by-channel"
              />
              <RollupList
                title="By feature (top 10)"
                rows={activeWindow?.by_feature}
                testidPrefix="admin-suppressions-by-feature"
              />
              <RollupList
                title="By reason"
                rows={activeWindow?.by_reason}
                testidPrefix="admin-suppressions-by-reason"
              />
            </section>

            {/* Filter bar */}
            <section className="cn-card p-5 space-y-3"
                     data-testid="admin-suppressions-filters">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                <Filter className="w-3.5 h-3.5" />
                Filters
              </div>
              <div className="grid gap-3 md:grid-cols-4">
                <label className="text-xs font-medium space-y-1 block">
                  <span>Channel</span>
                  <select
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                    className="w-full border border-cn-border rounded-lg px-2.5 py-2 bg-white dark:bg-cn-ink-900 text-sm"
                    data-testid="admin-suppressions-filter-channel">
                    {CHANNELS.map((c) => (
                      <option key={c || 'any'} value={c}>
                        {c || 'Any channel'}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-medium space-y-1 block">
                  <span>Feature</span>
                  <input
                    value={featureFilter}
                    onChange={(e) => setFeatureFilter(e.target.value)}
                    placeholder="e.g. rcm.weekly-digest"
                    className="w-full border border-cn-border rounded-lg px-2.5 py-2 bg-white dark:bg-cn-ink-900 text-sm"
                    data-testid="admin-suppressions-filter-feature"
                  />
                </label>
                <label className="text-xs font-medium space-y-1 block">
                  <span>Reason</span>
                  <input
                    value={reasonFilter}
                    onChange={(e) => setReasonFilter(e.target.value)}
                    placeholder="e.g. archived_feature"
                    className="w-full border border-cn-border rounded-lg px-2.5 py-2 bg-white dark:bg-cn-ink-900 text-sm"
                    data-testid="admin-suppressions-filter-reason"
                  />
                </label>
                <label className="text-xs font-medium space-y-1 block">
                  <span>Since</span>
                  <select
                    value={sinceHours}
                    onChange={(e) => setSinceHours(Number(e.target.value))}
                    className="w-full border border-cn-border rounded-lg px-2.5 py-2 bg-white dark:bg-cn-ink-900 text-sm"
                    data-testid="admin-suppressions-filter-since">
                    {SINCE_OPTIONS.map((o) => (
                      <option key={o.hours} value={o.hours}>{o.label}</option>
                    ))}
                  </select>
                </label>
              </div>
            </section>

            {/* Table */}
            <section className="cn-card overflow-hidden"
                     data-testid="admin-suppressions-table-card">
              <div className="p-5 flex items-center justify-between gap-2">
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                    Latest suppressions
                  </p>
                  <p className="text-sm text-cn-text-muted"
                     data-testid="admin-suppressions-count">
                    Showing {count} row{count === 1 ? '' : 's'}
                  </p>
                </div>
                <BellOff className="w-4 h-4 text-cn-text-muted" aria-hidden="true" />
              </div>
              <div className="overflow-x-auto border-t border-cn-border">
                <table className="min-w-full text-sm"
                       data-testid="admin-suppressions-table">
                  <thead className="bg-cn-bone-100 dark:bg-cn-ink-800 text-[11px] uppercase tracking-wider text-cn-text-muted">
                    <tr>
                      <th className="text-left px-4 py-2">When</th>
                      <th className="text-left px-4 py-2">Channel</th>
                      <th className="text-left px-4 py-2">Feature</th>
                      <th className="text-left px-4 py-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={4}
                            className="px-4 py-8 text-center text-sm text-cn-text-muted"
                            data-testid="admin-suppressions-empty">
                          No suppressed sends in this window — nice quiet loop.
                        </td>
                      </tr>
                    ) : rows.map((r, i) => (
                      <tr key={r.id || i}
                          className="border-t border-cn-border"
                          data-testid={`admin-suppressions-row-${i}`}>
                        <td className="px-4 py-2 tabular-nums text-xs text-cn-text-muted">
                          {r.created_at?.slice(0, 19).replace('T', ' ') || '—'}
                        </td>
                        <td className="px-4 py-2">
                          <span className="px-1.5 py-0.5 rounded bg-cn-bone-200 dark:bg-cn-ink-800 text-[11px] font-semibold">
                            {r.channel || 'notify'}
                          </span>
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">
                          {r.feature || <em className="text-cn-text-muted">untagged</em>}
                        </td>
                        <td className="px-4 py-2 text-xs">{r.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <p className="text-xs text-cn-text-muted" data-testid="admin-suppressions-footnote">
              Suppression events auto-purge after 90 days. The write is
              fire-and-forget from the notification gate so a slow Mongo
              never blocks the caller.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
